import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card } from '@/components/ui/card';

export type VideoMode = 'text-to-video' | 'image-to-video' | 'generated-image-to-video';

interface VideoModeSelectorProps {
  value: VideoMode;
  onChange: (mode: VideoMode) => void;
}

/**
 * VideoModeSelector - Choose video generation method
 *
 * Modes:
 * - Text-to-Video: Generate video from text description
 * - Image-to-Video: Animate an existing image
 * - Generated-Image-to-Video: Generate image then animate it (two-step)
 */
export const VideoModeSelector: React.FC<VideoModeSelectorProps> = ({ value, onChange }) => {
  const modes = [
    {
      id: 'text-to-video' as VideoMode,
      label: 'Text-to-Video',
      description: 'Generate a video directly from your text description',
      icon: '📝'
    },
    {
      id: 'image-to-video' as VideoMode,
      label: 'Image-to-Video',
      description: 'Animate an existing image from your library',
      icon: '🖼️'
    },
    {
      id: 'generated-image-to-video' as VideoMode,
      label: 'AI Image-to-Video',
      description: 'Generate an AI image first, then animate it',
      icon: '✨'
    }
  ];

  return (
    <div className="space-y-3">
      <Label>Video Generation Mode</Label>
      <RadioGroup value={value} onValueChange={(val) => onChange(val as VideoMode)}>
        <div className="grid gap-3">
          {modes.map((mode) => (
            <Card
              key={mode.id}
              className={`relative cursor-pointer transition-all hover:border-primary ${
                value === mode.id ? 'border-primary bg-primary/5' : ''
              }`}
              onClick={() => onChange(mode.id)}
            >
              <div className="flex items-start gap-3 p-4">
                <RadioGroupItem value={mode.id} id={mode.id} className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{mode.icon}</span>
                    <Label htmlFor={mode.id} className="cursor-pointer font-semibold">
                      {mode.label}
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{mode.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default VideoModeSelector;
